import { formatGroupId } from '@topspin/shared';
import type { SeasonView } from '../data.js';
import { useIsStandalone, useOnline } from '../hooks.js';
import { useJoinGroup } from '../join.js';
import { clearPlayerId, getKeys, removeKey } from '../storage.js';
import CalendarCard from '../components/CalendarCard.js';
import InstallSteps from '../components/InstallSteps.js';
import GroupIdField from './GroupIdField.js';

export default function Settings({ view, playerId }: { view: SeasonView; playerId: string }) {
  const form = useJoinGroup();
  const standalone = useIsStandalone();
  const online = useOnline();

  const me = view.groups
    .flatMap((g) => g.config.players)
    .find((p) => p.id === playerId);

  /**
   * Stored IDs that didn't come back as a group — revoked, or not part of this season. Without
   * a home here they'd be invisible and unremovable, since the gate only appears when *every*
   * stored ID fails (§5).
   */
  const stale = getKeys().filter((key) => !view.groups.some((g) => g.key === key));

  function forget(key: string) {
    removeKey(key);
    window.location.reload();
  }

  return (
    <>
      <h1>Settings</h1>

      <section className="card">
        <h2>You</h2>
        <p>
          Signed in as <strong>{me?.name ?? playerId}</strong>
        </p>
        {/* Deliberately quiet: a correction path, not a user-switching feature (§5.8). */}
        <button
          type="button"
          className="link"
          onClick={() => {
            clearPlayerId();
            window.location.reload();
          }}
        >
          That's not me
        </button>
      </section>

      <section className="card">
        <h2>Groups</h2>
        <ul className="roster">
          {view.groups.map((group) => (
            <li key={group.config.groupId} className="group-row">
              <span>
                {group.config.name}
                {/* Shown so you can read your own ID back to whoever's joining. */}
                <br />
                <code className="muted">{formatGroupId(group.key)}</code>
              </span>
              <button type="button" className="link" onClick={() => forget(group.key)}>
                remove
              </button>
            </li>
          ))}
          {stale.map((key) => (
            <li key={key} className="group-row">
              <span>
                <code>{formatGroupId(key)}</code>
                <br />
                <span className="muted">no longer loading</span>
              </span>
              <button type="button" className="link" onClick={() => forget(key)}>
                remove
              </button>
            </li>
          ))}
        </ul>
        {/* Jeff plays both nights; anyone in a second group adds it here (§5.6). */}
        <GroupIdField form={form} label="Add another group" action="Add group" />
      </section>

      {/* Below Groups: Settings is already the screen for "set this device up". */}
      <CalendarCard view={view} playerId={playerId} online={online} />

      {!standalone && (
        <section className="card">
          <h2>Add to your home screen</h2>
          <InstallSteps />
        </section>
      )}
    </>
  );
}
